/**
 * 从普通的文本中提炼出不同的文字块，包括@文字块，#跳转频道文字块，url文字块等
 */
import { TextNodePatternList, textNodePatternList, matchAll } from '_c/business/util/reg';
import { CommonLinkTextNodeType, EmojiItem, TextNode, TextNodeType } from '_c/typings/model/chat';
import { IMsg } from '_c/typings/model/im';
import { logger } from '_c/util/logger';
import { DartRegExpMatchArray } from '_c/typings/util';
import { MESSAGE_DESC } from '_c/constant/message';
import { MsgL2Type } from '_c/typings/proto';
import {
  ChannelLinkTextNode,
  CommonLinkTextNode,
  CommonLinkTextType,
  MentionMemberTextNode,
} from '_c/typings/model/chat/text-node';
import emojiList from '_c/config/json/emoticons.json';

const TAG = 'generateTextNode';

/**
 * 解析出
 * @param text
 * @param patternMap
 */
const extractTextMatchByPatternMap = (
  text: string,
  patternMap: TextNodePatternList,
): Array<DartRegExpMatchArray> => {
  const allMatches: Array<DartRegExpMatchArray> = [];
  Object.values(patternMap).forEach((pattern) => {
    const reg = new RegExp(pattern, 'g');
    // vivo x9i 7.1.2部分版本手机webview不支持String.prototype.matchAll，改为封装实现
    if (typeof String.prototype.matchAll !== 'undefined') {
      const mArr = text.matchAll(reg);
      for (const match of mArr) {
        allMatches.push(Object.assign(match, {
          pattern: reg,
        }));
      }
    } else {
      matchAll(text, reg).forEach((match) => {
        allMatches.push(Object.assign(match, {
          pattern: reg,
        }));
      });
    }
  });
  return allMatches;
};

/**
 * 创建普通文字元素
 * @param text
 */
function createNormalElement(text: string): Text {
  return document.createTextNode(text);
}

/**
 * 创建普通文字块
 * @param text
 */
const createNormalTextNode = (text: string): TextNode => ({
  type: TextNodeType.normal,
  text,
  innerText: '',
  displayText: text,
  element: createNormalElement(text),
});

/**
 * 创建链接文字块
 * @param text
 */
const createUrlTextNode = (text: string): TextNode => ({
  type: TextNodeType.url,
  text,
  innerText: '',
  displayText: text,
  element: createNormalElement(text),
});

const getEmojiPathFromSymbol = (symbolStr: string): string | null => {
  const target = (emojiList as Array<EmojiItem>).find(item => item.symbol === symbolStr);
  if (!target) return null;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const targetIconListContext = import.meta.globEager('../../business/chat/emoji/img/*.png');
  const targetIconKey = Object.keys(targetIconListContext).find(item => item.indexOf(`${target.name}.png`) > -1);
  if (!targetIconKey) return null;
  return targetIconListContext[targetIconKey].default;
};

const createEmotionElement = (text: string) => {
  const path = getEmojiPathFromSymbol(text);
  if (typeof path === 'string') {
    const img = document.createElement('img');
    img.src = path;
    img.className = 'emoji';
    img.dataset.symbol = text;
    img.addEventListener('click', () => false, false);
    img.tabIndex = -1;
    return img;
  }
  const btn = document.createElement('button');
  btn.className = 'emoji';
  btn.dataset.emoji = text;
  btn.textContent = text;
  btn.contentEditable = 'false';
  btn.addEventListener('click', () => false, false);
  btn.tabIndex = -1;
  return btn;
};

/**
 * 创建表情文字块
 * @param text
 */
const createEmotionTextNode = (text: string): TextNode => ({
  type: TextNodeType.emotion,
  text,
  innerText: '',
  displayText: text,
  element: createEmotionElement(text),
});

/**
 * 创建@元素
 * @param mentionMemberTextNode
 */
function createMentionMemberElement(mentionMemberTextNode: MentionMemberTextNode): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'mention-member';
  btn.dataset.mention = JSON.stringify(mentionMemberTextNode);
  btn.textContent = `@${mentionMemberTextNode.nickName}\u00A0`;
  btn.contentEditable = 'false';
  btn.addEventListener('click', () => false, false);
  btn.tabIndex = -1;
  return btn;
}

/**
 * 创建@文字块
 * @param text
 * @param jsonStr
 */
const createMentionMemberTextNode = (text: string, jsonStr: string): TextNode => {
  let isValid = true;
  let map: Record<string, unknown> = {};
  try {
    map = JSON.parse(jsonStr);
  } catch (err) {
    isValid = false;
    logger.error(TAG, 'JSON.parse fail, err: ', err);
  }
  const displayText = map.nickName ? `@${map.nickName}` : '';

  return {
    type: TextNodeType.mentionMember,
    text,
    innerText: jsonStr,
    displayText,
    element: createMentionMemberElement({
      uid: Number(map.uid) as number,
      nickName: map.nickName as string ?? '',
    }),
    isValid,
  };
};

const createChannelLinkTextNode = (channelLinkTextNode: ChannelLinkTextNode): HTMLButtonElement => {
  const btn = document.createElement('button');
  btn.className = 'channel-link';
  const ext: CommonLinkTextNode = {
    type: CommonLinkTextType.channelLink,
    displayName: `#${channelLinkTextNode.cname}`,
    ext: channelLinkTextNode,
  };
  btn.dataset.channel = JSON.stringify(ext);
  btn.textContent = `#${channelLinkTextNode.cname}\u00A0`;
  btn.contentEditable = 'false';
  btn.addEventListener('click', () => false, false);
  btn.tabIndex = -1;
  return btn;
};

/**
 * 创建#链接文字块
 * @param text
 * @param jsonStr
 */
const createCommonLinkTextNode = (text: string, jsonStr: string): TextNode => {
  let isValid = true;
  let map: Record<string, unknown> = {};
  try {
    map = JSON.parse(jsonStr);
  } catch (err) {
    isValid = false;
    logger.error(TAG, 'JSON.parse fail, err: ', err);
  }
  const type = Number(map.type) || 0;
  const { displayName } = map;
  let n = '';
  if (type > 0 && displayName) {
    if (type === CommonLinkTextNodeType.channelLink) {
      n += `# ${displayName}`;
    } else {
      n += `${displayName}`;
    }
  }
  const { ext } = map as {ext: Record<string, unknown>};
  let element: HTMLButtonElement | Text;
  if (ext && type === CommonLinkTextType.channelLink) {
    element = createChannelLinkTextNode({
      gid: Number(ext.gid),
      cid: String(ext.cid),
      cname: ext.cname as string,
    });
  } else {
    element = document.createTextNode('');
  }
  return {
    type: TextNodeType.commonLink,
    text,
    innerText: jsonStr,
    displayText: n,
    element,
    isValid,
  };
};

/**
 * 创建图片文字块
 */
const createPictureTextNode = (jsonStr: string): TextNode => {
  // 进入该函数只有，##频道，##图片
  let isValid;
  let map: Record<string, unknown> = {};
  try {
    map = JSON.parse(jsonStr);
  } catch (err) {
    isValid = false;
    logger.error('createPictureTextNode', 'JSON.parse fail, err: ', err);
  }
  // ##图片是否合格看fileUrl是否有
  const { fileUrl } = map;
  isValid = !!fileUrl;
  return {
    type: TextNodeType.picture,
    text: '当前版本暂不支持',
    innerText: '当前版本暂不支持',
    displayText: '当前版本暂不支持',
    element: document.createTextNode(''),
    isValid,
  };
};

/**
 * 创建bot命令文字块
 */
const createBotCommandTextNode = (): TextNode => ({
  type: TextNodeType.botCommand,
  text: '当前版本暂不支持',
  innerText: '当前版本暂不支持',
  displayText: '当前版本暂不支持',
  element: document.createTextNode(''),
});

/**
 * 根据正则匹配列表生成文字段落列表
 * @param text
 * @param allMatches
 */
const createTextNodeByMatchList = (
  text: string,
  allMatches: Array<DartRegExpMatchArray>,
): Array<TextNode> => {
  let currentIndex = 0;
  const textSpanList: Array<TextNode> = [];
  const urlPatternString = `${new RegExp(textNodePatternList.URL_PATTERN, 'g')}`;
  const emotionPatternString = `${new RegExp(textNodePatternList.EMOTION_PATTERN, 'g')}`;
  const mentionMemberPatternString = `${new RegExp(textNodePatternList.MENTION_MEMBER_PATTERN, 'g')}`;
  const commonLinkPatternString = `${new RegExp(textNodePatternList.COMMON_LINK_PATTERN, 'g')}`;
  const picturePatternString = `${new RegExp(textNodePatternList.PICTURE_PATTERN, 'g')}`;
  const botCommandPatternString = `${new RegExp(textNodePatternList.BOT_COMMAND_PATTERN, 'g')}`;
  allMatches.forEach((match) => {
    if (currentIndex <= Number(match.index)) {
      // 小于第一个match的开始时，当做纯文字处理
      {
        const tempStr = text.substring(currentIndex, match.index);
        if (tempStr.length > 0) {
          textSpanList.push(createNormalTextNode(tempStr));
          currentIndex = (match.index as number);
        }
      }
      // 开始进行文字组件的列表生成，原则上，如果有不同正则重合的问题，以list中出现的顺序为准，前者覆盖后者，最终分割出完全没有重合的字符列表
      {
        const tempStr = text.substring(currentIndex, currentIndex + match[0].length);
        const patternStr = `${match.pattern}`;
        let textSpan: TextNode;
        if (patternStr === urlPatternString) {
          // 检测链接文字，例如 https://qq.com
          textSpan = createUrlTextNode(tempStr);
        } else if (patternStr === emotionPatternString) {
          // 表情文字例如：[开心]，[可爱]
          textSpan = createEmotionTextNode(tempStr);
        } else if (patternStr === mentionMemberPatternString) {
          // 检测@消息，例如 @所有人，@xxx
          textSpan = createMentionMemberTextNode(tempStr, match[1]);
        } else if (patternStr === commonLinkPatternString) {
          // 检测通用跳转消息，例如 # 闲聊灌水，# 欢迎大厅
          textSpan = createCommonLinkTextNode(tempStr, match[1]);
        } else if (patternStr === picturePatternString) {
          // 检测图片消息纹理，暂不支持
          textSpan = createPictureTextNode(match[1]);
        } else if (patternStr === botCommandPatternString) {
          // 检测bot命令，暂不支持
          textSpan = createBotCommandTextNode();
        } else {
          textSpan = createNormalTextNode(tempStr);
        }
        if (textSpan?.isValid === false) return;
        textSpanList.push(textSpan);
      }
      currentIndex += match[0].length;
    }
  });
  // 4. 如果最后还有剩余的文字，直接按照普通文本进行处理
  if (currentIndex < text.length) {
    const tempStr = text.substring(currentIndex, text.length);
    const textSpan = createNormalTextNode(tempStr);
    textSpanList.push(textSpan);
    currentIndex = text.length;
  }
  // 5. 返回textSpan列表
  return textSpanList;
};

/**
 * 解析文字组件
 * @param text
 */
const extractTextNodeList = (text: string): Array<TextNode> => {
  // 1. 通过正则匹配检测出目标文字内所有的纹理样式
  let allMatches: Array<DartRegExpMatchArray> = extractTextMatchByPatternMap(text, textNodePatternList);
  // 2. 对所有匹配进行sort排序
  allMatches = allMatches.sort((a, b) => Number(a.index) - Number(b.index));
  // 3. 循环遍历所有的match，生成textNode列表
  return createTextNodeByMatchList(text, allMatches);
};

/**
 * 解析显示文字
 * @param text
 */
const extractDisplayText = (text: string): string => {
  const textSpanList = extractTextNodeList(text);
  return textSpanList.reduce((total, cur) => total + cur.displayText, '');
};

/**
 * 从消息中提取desc
 * @param message 消息
 * @returns 消息对应的desc
 */
function extractDescFromMessage(message: IMsg): string {
  const { desc, content, l2Type } = message;
  // 如果有desc字段则直接使用，该情况适用于消息已经经过后台处理
  if (desc) {
    return extractDisplayText(desc);
  }
  // 如果后台没有处理，则由前端先暂时填补，等待im收到消息后覆盖即可
  const strategyMap: Record<number, () => string> = {
    [MsgL2Type.MsgL2TypeText]: () => extractDisplayText(content) || MESSAGE_DESC.text,
    [MsgL2Type.MsgL2TypeAudio]: () => MESSAGE_DESC.audio,
    [MsgL2Type.MsgL2TypeFile]: () => MESSAGE_DESC.file,
    [MsgL2Type.MsgL2TypeFile]: () => MESSAGE_DESC.file,
    [MsgL2Type.MsgL2TypeMarkdown]: () => extractDisplayText(content) || MESSAGE_DESC.markdown,
    [MsgL2Type.MsgL2TypeSystem]: () => MESSAGE_DESC.system,
    [MsgL2Type.MsgL2TypePic]: () => MESSAGE_DESC.picture,
    [MsgL2Type.MsgL2TypeSticker]: () => MESSAGE_DESC.sticker,
    [MsgL2Type.MsgL2TypeVideo]: () => MESSAGE_DESC.video,
    [MsgL2Type.MsgL2TypeForward]: () => MESSAGE_DESC.history,
    [MsgL2Type.MsgL2TypeRoleCard]: () => MESSAGE_DESC.roleCard,
    [MsgL2Type.MsgL2TypeGraffiti]: () => MESSAGE_DESC.graffiti,
    [MsgL2Type.MsgL2TypeRichText]: () => MESSAGE_DESC.richText,
  };
  return strategyMap[l2Type]?.call(undefined) || MESSAGE_DESC.unsupported;
}

function transferTextToElementList(text: string): Array<HTMLElement | Text> {
  const tempArr: Array<HTMLElement | Text> = [];
  const textSpanList = extractTextNodeList(text);
  textSpanList.forEach((item) => {
    tempArr.push(item.element);
  });
  return tempArr;
}

export {
  extractTextNodeList,
  transferTextToElementList,
  extractDisplayText,
  extractDescFromMessage,
};
